// ============================================================
// SpringBootRelationships.jsx
//
// A branded, responsive course content page for the
// "Entity Relationships in Spring Boot" lesson.
//
// Author: ProDev Enterprise Series (Java Branch)
// Features: JPA relationship mapping, cascade logic,
//           and fetch strategy optimizations.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import AccountTreeIcon        from "@mui/icons-material/AccountTree";
import MediationIcon          from "@mui/icons-material/Mediation";
import LibraryBooksIcon       from "@mui/icons-material/LibraryBooks";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import SpeedIcon              from "@mui/icons-material/Speed";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function SpringBootRelationships() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Java Enterprise · Module 10"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Entity <span>Relationships</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Domain modeling is the heart of enterprise applications. Spring Data JPA allows you to define complex database associations using intuitive annotations, handling the underlying Foreign Key constraints and join tables automatically.
                    </Typography>
                </Box>

                {/* 1. MAPPING TYPES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AccountTreeIcon />} label="1. Relational Mapping Types" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        JPA provides four primary annotations to mirror SQL relationships. Choosing the right one is essential for database normalization and performance.
                    </Typography>
                    <Grid container spacing={2}>
                        {[
                            { title: "@OneToOne", desc: "A strict 1:1 link, like a User and their Profile." },
                            { title: "@ManyToOne", desc: "The child side of an association (e.g., Post -> User)." },
                            { title: "@OneToMany", desc: "The parent side of an association (e.g., User -> Posts)." },
                            { title: "@ManyToMany", desc: "Requires a join table (e.g., Students <-> Courses)." }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Paper sx={{ p: 2, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                                    <Typography sx={{ color: BRAND.gold, fontFamily: "monospace", fontWeight: 700, mb: 0.5 }}>{item.title}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 2. BIDIRECTIONAL MAPPING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<MediationIcon />} label="2. Bidirectional Associations" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        When mapping bidirectional relationships, use <code style={{color: BRAND.white}}>mappedBy</code> to tell JPA that the other side owns the relationship, preventing duplicate Foreign Keys.
                    </Typography>
                    <CodeBlock
                        title="Parent & Child Entities"
                        code={`// User.java (Inverse Side)\n@OneToMany(mappedBy = "user", cascade = CascadeType.ALL)\nprivate List<Post> posts;\n\n// Post.java (Owning Side)\n@ManyToOne\n@JoinColumn(name = "user_id") // Maps the FK column\nprivate User user;`}
                    />
                </Box>

                {/* 3. PERSISTENCE CASCADE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LibraryBooksIcon />} label="3. Cascading Operations" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Cascades define how operations on a parent entity affect its children. For example, deleting a User should likely delete their Posts automatically.
                    </Typography>
                    <CodeBlock
                        title="Cascade Strategies"
                        code={`CascadeType.ALL     // Propagate all operations\nCascadeType.PERSIST // Propagate save only\nCascadeType.REMOVE  // Propagate delete only`}
                    />
                </Box>

                {/* 4. FETCH STRATEGIES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SpeedIcon />} label="4. EAGER vs LAZY Fetching" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Control when related data is loaded from the database. <strong>LAZY</strong> loading is the gold standard for performance to prevent fetching massive object trees unnecessarily.
                    </Typography>
                    <CodeBlock
                        title="Fetching Configurations"
                        code={`// Load only when .getPosts() is called\n@OneToMany(fetch = FetchType.LAZY)\n\n// Always load User when Post is fetched\n@ManyToOne(fetch = FetchType.EAGER)`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* HIGHLIGHTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Implementation Best Practices" />
                    <Grid container spacing={1.5}>
                        {[
                            "Always use LAZY loading for collections to avoid N+1 issues",
                            "Use 'mappedBy' on the Inverse side of @OneToMany",
                            "Explicitly define @JoinColumn names for database clarity",
                            "Use CascadeType.ALL sparingly; be careful with automatic deletions",
                            "Use Set instead of List for ManyToMany to prevent duplicates",
                            "Avoid deep circular dependencies to prevent serialization errors"
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/java-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> When using <strong>LAZY</strong> loading, beware of the <code>LazyInitializationException</code>. This happens if you try to access a collection after the Hibernate session (transaction) has closed. Use <code>@Transactional</code> to keep the session open.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
